import { createMachine, assign } from 'xstate';
import { getUserLists, addressList } from 'ggApp/shared/context/Order/services/common/getUserLists';
import { userAddress } from 'ggApp/shared/context/Order/services/common/userAddress';

import { PhoneVerificationPendingType } from 'ggApp/shared/context/Order/types';

type Item = {
    image?: string;
    coreAttribute?: string;
    name?: string;
};

interface SwapContext {
    newItem?: Item | {};
    currentItems?: any;
    userAddresses: [];
    user: {};
    orderNumber?: string;
    addressError?: string;
    phoneVerificationPending?: PhoneVerificationPendingType | {};
    phoneVerificationConfirmError?: any;
}

export const swapMachine = createMachine<SwapContext>(
    {
        id: 'swap',
        initial: 'idle',
        context: {
            user: {},
            newItem: {},
            currentItems: [],
            userAddresses: [],
            orderNumber: undefined,
            addressError: undefined,
            phoneVerificationConfirmError: null,
            phoneVerificationPending: {},
        },
        on: {
            CLOSE: 'idle',
            UPDATE_SWAP_ITEMS: {
                actions: assign({
                    newItem: (context, event: { newItem?: Item } = {}) =>
                        event?.newItem ?? context?.newItem,
                    currentItems: (context, event: { oldItems?: any } = {}) =>
                        event?.oldItems ?? context?.currentItems,
                }),
            },
            SET_USER: {
                target: 'fetchAddresses',
                actions: ['updateUser'],
            },
        },
        states: {
            intro: {
                on: {
                    NEXT: 'selectSwap',
                },
            },

            fetchAddresses: {
                invoke: {
                    src: getUserLists.addresses,
                    onDone: {
                        target: 'intro',
                        actions: ['updateUserAddresses'],
                    },
                },
            },

            idle: {
                on: {
                    NEXT: 'intro',
                },
            },

            selectSwap: {
                on: {
                    NEXT: {
                        target: 'swapReview',
                        actions: assign({
                            orderNumber: (context, event) => event?.orderNumber,
                        }),
                    },
                },
            },

            swapReview: {
                id: 'swapReview',
                on: {
                    NEXT: 'confirmation',
                    ERROR: 'errorScreen',
                    EDIT_ADDRESS: 'addressList',
                },
            },

            errorScreen: {
                on: {
                    NEXT: 'swapReview',
                },
            },

            addressList: {
                id: 'addressList',
                on: {
                    NEXT: 'swapReview',
                },
                initial: 'idle',
                states: {
                    idle: {
                        id: 'addressList.list',
                        on: {
                            EDIT: 'add',
                            CHANGE_ADDRESS: 'change',
                        },
                    },
                    add: {
                        id: 'addressList.add',
                        initial: 'idle',
                        on: {
                            BACK: 'idle',
                            UPDATE: '.update',
                            CREATE: '.create',
                        },
                        states: {
                            idle: {},
                            update: {
                                invoke: {
                                    src: addressList.update,
                                    onDone: {
                                        target: '#addressList.add',
                                        actions: ['updateUserAddresses'],
                                    },
                                    onError: {
                                        target: '#addressList.add',
                                        actions: ['updateAddressError'],
                                    },
                                },
                            },
                            create: {
                                invoke: {
                                    src: addressList.add,

                                    onDone: {
                                        target: '#addressList.list',
                                        actions: ['updateUserAddresses'],
                                    },

                                    onError: [
                                        {
                                            target: '#phoneVerification',
                                            cond: 'twoFaRequired',
                                            actions: [
                                                'updateAddressError',
                                                'updatePhoneVerificationPending',
                                            ],
                                        },
                                        {
                                            target: 'idle',
                                            actions: ['updateAddressError'],
                                        },
                                    ],
                                },
                            },
                        },
                    },
                    change: {
                        id: 'shippingAddressUpdate',
                        invoke: {
                            src: userAddress.updateShipping,
                            onDone: {
                                target: '#swapReview',
                                actions: ['updateUser'],
                            },
                        },
                    },
                },
            },

            confirmation: {
                on: {
                    NEXT: 'idle',
                },
            },

            phoneVerification: {
                initial: 'idle',
                id: 'phoneVerification',
                states: {
                    idle: {
                        on: {
                            CONFIRM: 'confirm',
                            HELP: '#2faHelp',
                        },
                    },
                    confirm: {
                        invoke: [
                            {
                                src: addressList.add,
                                onDone: {
                                    target: '#addressList.list',
                                    actions: ['updateUserAddresses', 'clearPhoneVerification'],
                                },
                                onError: {
                                    target: 'idle',
                                    actions: ['updatePhoneVerificationConfirmError'],
                                },
                            },
                        ],
                    },
                },
            },

            twoFaHelp: {
                initial: 'idle',
                id: '2faHelp',
                states: {
                    idle: {
                        on: {
                            RESEND_CODE: 'resendCode',
                        },
                    },
                    resendCode: {
                        invoke: {
                            src: addressList.add,
                            onError: {
                                target: '#phoneVerification',
                                actions: ['updatePhoneVerificationPending'],
                            },
                        },
                    },
                },
                on: {
                    BACK: {
                        target: '#phoneVerification',
                    },
                },
            },
        },
    },
    {
        actions: {
            updateUser: assign({
                user: (context, event: { data?: object } = {}) => event.data,
            }),
            updateUserAddresses: assign({
                userAddresses: (context: any, event: { data?: Array<any>; type?: string } = {}) =>
                    event.data || [],
            }),
            updateAddressError: assign({
                addressError: (context: any, event: { data: any }) =>
                    event.data?.response?.data?.base ?? null,
            }),
            updatePhoneVerificationPending: assign({
                phoneVerificationPending: (context: any, event: { data: any; type: string }) => ({
                    ...context.phoneVerificationPending,
                    requestPayload: JSON.parse(event.data?.config?.data ?? '{}'),
                    timestamp: Date.now(),
                    timeoutSeconds: event.data?.response?.data?.resend_code_timeout_seconds,
                    phoneLastDigits: event.data?.response?.data?.phone_last_digits,
                }),
            }),
            clearPhoneVerification: assign({
                phoneVerificationConfirmError: () => null,
                phoneVerificationPending: () => ({}),
            }),
            updatePhoneVerificationConfirmError: assign({
                phoneVerificationConfirmError: (context, event: { data: any }) =>
                    event.data?.response?.data?.base ?? null,
            }),
        },

        guards: {
            twoFaRequired: (context, event: any) =>
                event.data?.response?.status === 422 &&
                event.data?.response?.data?.['2fa_required'] === true,
        },
    },
);
